<script lang="ts" setup>
import {
  shallowRef,
  computed,
  watch,
  type VNode,
  type VNodeProps,
  type ComponentPublicInstance,
  nextTick,
  onBeforeUnmount,
} from 'vue';
import {
  useFloating,
  flip,
  autoUpdate,
  shift,
  offset,
  size,
  autoPlacement,
  hide,
  type Middleware,
  type ShiftOptions,
  type OffsetOptions,
  type FlipOptions,
  type SizeOptions,
  type Placement,
  type AutoPlacementOptions,
} from '@floating-ui/vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { onClickOutside, unrefElement } from '@vueuse/core';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useZIndex, useFocusScope } from '../../composables';
import { ObUnmountableTeleportWithTransition } from '../internal';

interface Props {
  active: boolean;
  overlay?: boolean;
  placement?: Placement;
  padding?:
    | number
    | Partial<{
        top: number;
        right: number;
        bottom: number;
        left: number;
      }>;
  offset?: OffsetOptions;
  shift?: boolean | ShiftOptions;
  flip?: boolean | FlipOptions;
  size?: SizeOptions;
  autoPlacement?: boolean | AutoPlacementOptions;
  focusTrap?: boolean;
}

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<Props>();

const emit = defineEmits<{
  clickOutside: [event: PointerEvent];
}>();

defineSlots<{
  default?: () => VNode;
  host?: (props: {
    active: boolean;
    hostProps: VNodeProps & { ref: (el: Element | ComponentPublicInstance | null) => void };
  }) => VNode;
}>();

const active = defineModel<boolean>('active', { default: false });

const { zIndex } = useZIndex({ active, size: 2 });

const hostRef = shallowRef<HTMLElement | null>(null);
const containerRef = shallowRef<HTMLDivElement | null>(null);
const overlayRef = shallowRef<HTMLDivElement | null>(null);

// TODO:
// support middleware
// https://floating-ui.com/docs/hide
// https://floating-ui.com/docs/arrow
// https://floating-ui.com/docs/inline

const middleware = computed<Middleware[]>(() => {
  const result: Middleware[] = [
    // hide({ padding: props.padding, strategy: 'escaped' }),
    hide({ padding: props.padding, strategy: 'referenceHidden' }),
  ];

  if (props.offset) {
    result.push(offset(props.offset));
  }

  if (props.shift) {
    result.push(
      shift({
        padding: props.padding,
        ...(typeof props.shift === 'object' ? props.shift : {}),
      }),
    );
  }

  if (props.flip) {
    result.push(
      flip({
        padding: props.padding,
        ...(typeof props.flip === 'object' ? props.flip : {}),
      }),
    );
  } else if (props.autoPlacement) {
    result.push(
      autoPlacement({
        padding: props.padding,
        ...(typeof props.autoPlacement === 'object' ? props.autoPlacement : {}),
      }),
    );
  }

  if (props.size) {
    result.push(size({ padding: props.padding, ...props.size }));
  }

  return result;
});

const placement = computed(() => props.placement || 'bottom-start');

const { floatingStyles, middlewareData } = useFloating(hostRef, containerRef, {
  placement,
  middleware,
  whileElementsMounted: autoUpdate,
  open: active,
  transform: false,
});

const { active: focusScopeActive } = useFocusScope(containerRef);

const { activate: activateTrap, deactivate: deactivateTrap } = useFocusTrap(containerRef, {
  escapeDeactivates: false,
  allowOutsideClick: true,
  returnFocusOnDeactivate: false,
  clickOutsideDeactivates: (event: Event) => {
    const hostEl = unrefElement(containerRef);
    return !hostEl || hostEl !== event.target || !hostEl.contains(event.target as Element);
  },
  fallbackFocus: () => {
    return unrefElement(containerRef) ?? document.body;
  },
});

onClickOutside(
  containerRef,
  (event: PointerEvent) => {
    setTimeout(() => {
      if (!focusScopeActive.value) {
        emit('clickOutside', event);
      }
    }, 1);
  },
  { ignore: [hostRef, overlayRef] },
);

function onOverlayClick() {
  active.value = false;
  nextTick(() => {
    hostRef.value?.focus();
  });
}

const hostProps = computed(() => {
  return {
    ref: (el: Element | ComponentPublicInstance | null) => {
      hostRef.value = unrefElement(el as any); // TODO: how not to cast no any? Element vs HTMLElement
    },
  };
});

defineExpose({
  $el: containerRef,
});

function lockScroll() {
  if (containerRef.value) {
    disableBodyScroll(containerRef.value, {
      reserveScrollBarGap: true,
    });
  }
}

function unlockScroll() {
  if (containerRef.value) {
    enableBodyScroll(containerRef.value);
  }
}

watch(
  active,
  (value) => {
    if (value) {
      nextTick(() => {
        if (active.value) {
          if (props.focusTrap) {
            activateTrap();
          }

          if (props.overlay) {
            lockScroll();
          }
        }
      });

      return;
    }

    unlockScroll();
    deactivateTrap();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  unlockScroll();
});
</script>

<template>
  <slot name="host" v-bind="{ hostProps, active }" />
  <Teleport v-if="overlay && active" to="body" defer>
    <div ref="overlayRef" :class="$style.overlay" :style="{ zIndex }" tabindex="-1" @click="onOverlayClick" />
  </Teleport>
  <ObUnmountableTeleportWithTransition
    :active
    :enter-from-class="$style.enterFrom"
    :enter-active-class="$style.enterActive"
    :leave-active-class="$style.leaveActive"
    :leave-to-class="$style.leaveTo"
  >
    <div
      v-if="active"
      ref="containerRef"
      :style="{
        ...floatingStyles,
        zIndex: zIndex! + 1,
        visibility: middlewareData.hide?.escaped || middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
      }"
      tabindex="-1"
      :class="$style.container"
      v-bind="$attrs"
    >
      <slot />
    </div>
  </ObUnmountableTeleportWithTransition>
</template>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/shared';
@use '../../styles/typography';

.enterActive,
.leaveActive {
  transition-property: opacity, transform;
  transition-duration: 0.2s;
}

.enterActive {
  transition-timing-function: ease-out;
}

.leaveActive {
  transition-timing-function: ease-in;
}

.enterFrom,
.leaveTo {
  opacity: 0;
  transform: scale(0.9);
}

.container {
  box-sizing: border-box;
  max-height: 480px;
  width: auto;
  border-radius: 12px;
  background: #fff;
  color: colors.$primary;
  border: 1px solid colors.$surface-6;
  box-shadow: 0px 0px 18px 0px rgba(2, 17, 72, 0.2);
  font-family: typography.$font-family-primary;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.overlay {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(#000, 0.1);
}
</style>
